import createBackoff from '@livechat/backoff';

/**
 * Retries promise factory until it resolves or maximum retries count is reached
 * @param promiseFactory - function that returns a promise
 * @param options - options object
 * @param options.retriesCount - maximum retries count, default: Infinity
 * @param options.minTime - minimum time between retries, default: 100
 * @param options.maxTime - maximum time between retries, default: 10000
 * @returns promise that resolves when promiseFactory resolves or rejects when maximum retries count is reached
 * @example
 * const promise = promiseRetry(() => fetch('https://example.com'), { retriesCount: 3 })
 * promise.then(response => console.log(response)).catch(error => console.log(error))
 */

function promiseRetry(promiseFactory, _temp) {
  var _ref = _temp === void 0 ? {} : _temp,
    _ref$retriesCount = _ref.retriesCount,
    retriesCount = _ref$retriesCount === void 0 ? Infinity : _ref$retriesCount,
    _ref$minTime = _ref.minTime,
    minTime = _ref$minTime === void 0 ? 100 : _ref$minTime,
    _ref$maxTime = _ref.maxTime,
    maxTime = _ref$maxTime === void 0 ? 10000 : _ref$maxTime;
  var backoff = createBackoff({
    min: minTime,
    max: maxTime,
    jitter: 0
  });
  return new Promise(function (resolve, reject) {
    var count = 0;
    var attempt = function attempt() {
      return promiseFactory().then(resolve, function () {
        if (retriesCount === Infinity || count++ < retriesCount) {
          setTimeout(attempt, backoff.duration());
        } else {
          reject(new Error("Maximum retries count (" + retriesCount + ") reached"));
        }
      });
    };
    attempt();
  });
}

/**
 * promiseTry allows us to move success / error handling to be promise based so we will handle both synchronous and asynchronous execution failures and success properly.
 * @param anyFunction - function to be called
 * @returns promise that resolves to the return value of the function
 * @example
 * const promise = promiseTry(() => 1)
 * promise.then(value => console.log(value)) // 1
 */

function promiseTry(anyFunction) {
  return new Promise(function (resolve) {
    resolve(anyFunction());
  });
}

/**
 * promiseQueue is a function that returns an object with two methods: add and flush.
 * add takes a function that will be added to the queue and returns a promise that resolves to the function's return value when it is called and executed in order of addition to the queue.
 * flush takes an error and rejects all pending promises with that error.
 * @returns object with add and flush methods
 * @example
 * const queue = promiseQueue()
 * const promise1 = queue.add(() => 1)
 * const promise2 = queue.add(() => 2)
 * promise1.then(value => console.log(value)) // 1
 * promise2.then(value => console.log(value)) // 2
 */

function promiseQueue() {
  var queue = [];
  var isPending = false;
  var run = function run() {
    var _queue$ = queue[0],
      task = _queue$.task,
      resolve = _queue$.resolve,
      reject = _queue$.reject;
    promiseTry(task).then(resolve, reject).then(next);
  };
  var next = function next() {
    queue.shift();
    if (queue.length === 0) {
      isPending = false;
      return;
    }
    run();
  };
  return {
    add: function add(task) {
      return new Promise(function (resolve, reject) {
        var item = {
          task: task,
          resolve: resolve,
          reject: reject
        };
        queue.push(item);
        if (isPending) {
          return;
        }
        isPending = true;
        run();
      });
    },
    flush: function flush(reason) {
      var oldQueue = queue;
      queue = [];
      isPending = false;
      oldQueue.forEach(function (task) {
        task.reject(reason);
      });
    }
  };
}

/**
 * Creates a deferred object that can be resolved or rejected later.
 * @returns A deferred object with a promise and resolve and reject functions.
 * @example
 * const def = promiseDeferred()
 * const promise = def.promise
 * def.resolve(1)
 * return promise.then(res => console.log(res)) // 1
 */

function promiseDeferred() {
  var deferred = {};
  deferred.promise = new Promise(function (resolve, reject) {
    deferred.resolve = resolve;
    deferred.reject = reject;
  });
  return deferred;
}

var abortableTimeout = function abortableTimeout(callback, timeout, signal) {
  var timeoutId;
  var timeoutPromise = new Promise(function (resolve) {
    timeoutId = setTimeout(resolve, timeout);
  });
  var cleanup = function cleanup() {
    clearTimeout(timeoutId);
    signal.removeEventListener('abort', cleanup);
  };
  signal.addEventListener('abort', cleanup);
  return timeoutPromise.then(function () {
    cleanup();
    return callback();
  });
};

/**
 * promiseTimeoutController is a function that returns an object with two properties: racePromiseWithTimeout and signal.
 * The main function, racePromiseWithTimeout, takes a promise and a timeout duration as parameters.
 * It returns a promise that races the original promise with a timeout.
 * If the original promise resolves or rejects before the timeout, the timeout is aborted.
 * If the timeout occurs before the original promise completes, the request is aborted.
 * The function also exposes the signal from the requestAbortController for external use.
 * @returns object with racePromiseWithTimeout and signal properties
 * @example
 * const { racePromiseWithTimeout, signal } = promiseTimeoutController()
 * const promise = fetch('https://example.com', { signal })
 * const result = await racePromiseWithTimeout(promise, 1000)
 */

function promiseTimeoutController() {
  var requestAbortController = new AbortController();
  var timeoutAbortController = new AbortController();
  var racePromiseWithTimeout = function racePromiseWithTimeout(promise, timeout) {
    return Promise.race([promise["finally"](function () {
      timeoutAbortController.abort();
    }), abortableTimeout(function () {
      requestAbortController.abort();
      return null;
    }, timeout, timeoutAbortController.signal)]);
  };
  return {
    racePromiseWithTimeout: racePromiseWithTimeout,
    signal: requestAbortController.signal
  };
}

export { promiseDeferred, promiseQueue, promiseRetry, promiseTimeoutController, promiseTry };
